// ProductModal.jsx
import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Overlay,
  Modal,
  Frame,
  Header,
  Title,
  CloseBtn,
  HeaderOrnament,
  Top,
  ImgWrap,
  Meta,
  Price,
  Tabs,
  TabBar,
  Panel,
} from "./ProductModalStyles";

export default function ProductModal({ product, onClose, showPrice = true }) {
  const { t } = useTranslation(); // pentru cheile din "menu" și "reservation"
  const { t: tc } = useTranslation(); // ✅ pentru tab-uri din "common.tabs.*"

  const [tab, setTab] = useState("ingrediente");

  const hasPrice =
    showPrice &&
    product?.price !== undefined &&
    product?.price !== null &&
    product?.price !== "" &&
    !Number.isNaN(Number(product?.price));

  useEffect(() => {
    if (!product) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [product, onClose]);

  const nutritionBlock = useMemo(() => {
    const n = product?.nutrition;
    if (!n) return null;

    if (typeof n === "string") return <p>{n}</p>;

    const rows = [
      ["Kcal", n.kcal ?? n.calorii ?? n.energy],
      ["Proteine", n.proteins ?? n.proteine],
      ["Carbohidrați", n.carbs ?? n.carbohidrati],
      ["Grăsimi", n.fats ?? n.grasimi],
      ["Sare", n.salt ?? n.sodium],
      ["Fibră", n.fiber ?? n.fibra],
    ].filter(([, v]) => v !== undefined && v !== null && v !== "");

    if (!rows.length)
      return (
        <p>
          {tc(
            "tabs.noNutrition",
            "Informațiile nutriționale nu sunt disponibile."
          )}
        </p>
      );

    return (
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <td
                style={{
                  padding: "6px 8px",
                  borderBottom: "1px solid rgba(0,0,0,.06)",
                }}
              >
                {k}
              </td>
              <td
                style={{
                  padding: "6px 8px",
                  borderBottom: "1px solid rgba(0,0,0,.06)",
                  textAlign: "right",
                  fontWeight: 700,
                  color: "#8b8064",
                }}
              >
                {v}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }, [product, tc]);

  if (!product) return null;

  return (
    <Overlay onMouseDown={onClose}>
      <Modal
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t("menu.productModal.a11yLabel", "Detalii produs")}
      >
        <Frame>
          <Header>
            <CloseBtn
              onClick={onClose}
              aria-label={t("reservation.actions.close", "Închide")}
            />
            <Title>{product.name}</Title>
            <HeaderOrnament aria-hidden />
          </Header>

          <Top>
            <ImgWrap>
              {product.img ? (
                <img src={product.img} alt={product.name} />
              ) : null}
            </ImgWrap>

            <Meta>
              {hasPrice && (
                <Price>
                  {product.price} {t("menu.currency", "lei")}
                </Price>
              )}

              <Tabs>
                <TabBar>
                  <button
                    type="button"
                    data-active={tab === "ingrediente"}
                    onClick={() => setTab("ingrediente")}
                  >
                    {tc("tabs.ingredients", "Ingrediente")}
                  </button>

                  <button
                    type="button"
                    data-active={tab === "alergeni"}
                    onClick={() => setTab("alergeni")}
                  >
                    {tc("tabs.allergens", "Alergeni")}
                  </button>

                  <button
                    type="button"
                    data-active={tab === "nutritie"}
                    onClick={() => setTab("nutritie")}
                  >
                    {tc("tabs.nutrition", "Nutriție")}
                  </button>
                </TabBar>

                <Panel>
                  {tab === "ingrediente" && (
                    <p>
                      {Array.isArray(product.ingredients)
                        ? product.ingredients.join(", ")
                        : product.desc || "—"}
                    </p>
                  )}

                  {tab === "alergeni" &&
                    (Array.isArray(product.allergens) &&
                    product.allergens.length ? (
                      <p>{product.allergens.join(", ")}</p>
                    ) : (
                      <p>
                        {tc(
                          "tabs.noAllergens",
                          "Nu sunt specificați alergeni."
                        )}
                      </p>
                    ))}

                  {tab === "nutritie" &&
                    (nutritionBlock || (
                      <p>
                        {tc(
                          "tabs.noNutrition",
                          "Informațiile nutriționale nu sunt disponibile."
                        )}
                      </p>
                    ))}
                </Panel>
              </Tabs>
            </Meta>
          </Top>
        </Frame>
      </Modal>
    </Overlay>
  );
}
